import React from "react";
import { ArrowRight, Sparkles, MessageCircle, Check, ShieldCheck, Award, GraduationCap } from "lucide-react";
import { Button } from "./ui/button";
import { CONTACT } from "../data/mock";
import AnimatedCounter from "./AnimatedCounter";
import Hero3DLogo from "./Hero3DLogo";

const TRUST_BADGES = [
  { icon: ShieldCheck, label: "Aggregazione MIMIT" },
  { icon: Award, label: "Legge 4/2013" },
  { icon: GraduationCap, label: "CFU Federico II" },
];

export default function Hero() {
  return (
    <section id="home" className="relative pt-12 md:pt-16 pb-20 md:pb-28 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 grid-bg"></div>
      <div className="orb bg-[#102a6b]" style={{ width: 520, height: 520, top: -160, left: -120 }}></div>
      <div className="orb bg-[#f59e0b]" style={{ width: 460, height: 460, top: 60, right: -140, opacity: 0.35 }}></div>

      <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <div className="reveal">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/80 backdrop-blur border border-slate-200 text-[12px] font-semibold text-[#102a6b] shadow-sm">
            <Sparkles className="h-3.5 w-3.5 text-[#f59e0b]" />
            Corso certificato · Da € 99 · Iscrizione al Registro inclusa
          </div>

          <h1
            data-testid="hero-headline"
            className="mt-5 text-4xl md:text-5xl lg:text-[56px] leading-[1.05] font-extrabold text-[#0a1f55]"
          >
            Diventa <span className="shine-text">Consulente Digitale, Tecnologico e di Sostenibilità</span> certificato in 20 ore.
          </h1>

          <p className="mt-5 text-slate-600 text-lg leading-relaxed max-w-xl">
            Corso A.C.D.T. per la qualificazione professionale riconosciuto <strong>Legge 4/2013</strong>, iscrizione al{" "}
            <strong>Registro Nazionale</strong> Aggregazione MIMIT, tessera ufficiale e tutela
            digitale inclusa. Per privati, professionisti e aziende.
          </p>

          {/* Punti chiave */}
          <ul className="mt-5 space-y-2" data-testid="hero-key-points">
            {[
              "Tutela digitale per soci e consumatori",
              "Corsi interattivi con tracciamento della formazione",
              "Guide pratiche per professionisti, aziende e privati",
            ].map((p) => (
              <li key={p} className="flex items-start gap-2.5 text-[15px] font-semibold text-[#0a1f55]">
                <span className="mt-0.5 h-5 w-5 shrink-0 rounded-full bg-[#f59e0b]/15 flex items-center justify-center">
                  <Check className="h-3.5 w-3.5 text-[#b45309]" />
                </span>
                {p}
              </li>
            ))}
          </ul>

          {/* Trust badges (above the fold) */}
          <div className="mt-6 flex flex-wrap items-center gap-2.5" data-testid="hero-trust-bar">
            {TRUST_BADGES.map((b) => {
              const I = b.icon;
              return (
                <div
                  key={b.label}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-sm text-[12px] font-bold text-[#0a1f55]"
                >
                  <I className="h-3.5 w-3.5 text-[#f59e0b]" />
                  {b.label}
                </div>
              );
            })}
          </div>

          <div className="mt-7 flex flex-wrap gap-3">
            <a href="#pacchetti">
              <Button
                data-testid="hero-cta-primary"
                size="lg"
                className="bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1f55] px-6 h-12 font-extrabold shadow-lg shadow-[#f59e0b]/25"
              >
                Vedi i pacchetti · da € 99
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </a>
            <a href={CONTACT.whatsappChannel} target="_blank" rel="noreferrer">
              <Button
                data-testid="hero-cta-whatsapp"
                size="lg"
                variant="outline"
                className="h-12 border-slate-300 text-[#102a6b] hover:bg-[#102a6b]/5 hover:text-[#102a6b]"
              >
                <MessageCircle className="mr-2 h-4 w-4 text-[#25D366]" />
                Parla con noi
              </Button>
            </a>
          </div>

          {/* Stats */}
          <div className="mt-10 grid grid-cols-3 gap-5 max-w-lg">
            <div>
              <div className="text-3xl font-extrabold text-[#102a6b]">
                <AnimatedCounter end={20} />h
              </div>
              <div className="text-xs mt-1 uppercase tracking-wider text-slate-500">Durata corso</div>
            </div>
            <div>
              <div className="text-3xl font-extrabold text-[#102a6b]">
                <AnimatedCounter end={20} suffix="+" />
              </div>
              <div className="text-xs mt-1 uppercase tracking-wider text-slate-500">Moduli didattici</div>
            </div>
            <div>
              <div className="text-3xl font-extrabold text-[#102a6b]">
                365g
              </div>
              <div className="text-xs mt-1 uppercase tracking-wider text-slate-500">Validità tessera</div>
            </div>
          </div>
        </div>

        {/* Right visual */}
        <div className="relative reveal">
          <Hero3DLogo />
        </div>
      </div>
    </section>
  );
}

